import { useState } from "react";
import { useParams } from "react-router-dom";
import {
    Typography,
    Button,
    Stack,
    Card,
    CardContent,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
    Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { PremiumDataGrid } from "../components/PremiumDataGrid";

import { useCollection } from "../hooks/useCollection";
import ItemDialog from "../components/ItemDialog";
import { Addition } from "../interfaces/Addition";
import { useAuth } from "../hooks/useAuth";

export default function AdditionsPage() {
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { data, createItem, updateItem, deleteItem } =
        useCollection<Addition>(restaurantId!, "additions");

    const { canCreate, canEdit, canDelete } = useAuth();

    const { data: categories = [] } = useCollection<{ id: string; name: string }>(
        restaurantId!,
        "category"
    );

    const [open, setOpen] = useState(false);
    const [current, setCurrent] = useState<Addition | null>(null);

    const columns: GridColDef[] = [
        { field: "name", headerName: "Nombre", flex: 2 },
        { field: "category", headerName: "Categoría", flex: 1 },
        {
            field: "price",
            headerName: "Impacto Precio",
            flex: 1,
            renderCell: (params) => {
                const val = Number(params.value) || 0;
                const formatted = Math.abs(val).toLocaleString("es-CO");
                return (
                    <Typography color={val >= 0 ? "success.main" : "error.main"}>
                        {val >= 0 ? `+ $${formatted}` : `- $${formatted}`}
                    </Typography>
                );
            },
        },
        {
            field: "isIntrusive",
            headerName: "Intrusiva (Modal)",
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.value ? "⚡ Sí (Modal)" : "No"}
                    color={params.value ? "warning" : "default"}
                    size="small"
                    variant={params.value ? "filled" : "outlined"}
                />
            ),
        },
        {
            field: "actions",
            headerName: "Acciones",
            width: 160,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <Button
                        size="small"
                        disabled={!canEdit}
                        onClick={() => {
                            setCurrent(params.row as Addition);
                            setOpen(true);
                        }}
                    >
                        Editar
                    </Button>
                    <Button
                        size="small"
                        color="error"
                        disabled={!canDelete}
                        onClick={() => {
                            if (window.confirm(`¿Estás seguro de que deseas eliminar la adición "${params.row.name}"?`)) {
                                deleteItem(params.row.id);
                            }
                        }}
                    >
                        Eliminar
                    </Button>
                </Stack>
            ),
        },
    ];

    return (
        <>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                mb={2}
            >
                <Typography variant="h5">➕ Adiciones</Typography>

                {canCreate && (
                    <Button
                        variant="contained"
                        onClick={() => {
                            setCurrent(null);
                            setOpen(true);
                        }}
                    >
                        Agregar adición
                    </Button>
                )}
            </Stack>

            {!isMobile && (
                <Box sx={{ height: 520 }}>
                    <PremiumDataGrid
                        rows={data}
                        columns={columns}
                        getRowId={(row) => row.id!}
                        onRowDoubleClick={(p: GridRowParams) => {
                            setCurrent(p.row as Addition);
                            setOpen(true);
                        }}
                    />
                </Box>
            )}

            {isMobile && (
                <Stack spacing={2}>
                    {data.map((item) => (
                        <Card key={item.id}>
                            <CardContent>
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Box flex={1}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography fontWeight={600}>{item.name}</Typography>
                                            {item.isIntrusive && (
                                                <Chip label="⚡ Modal" color="warning" size="small" />
                                            )}
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            {item.category}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color={item.price >= 0 ? "success.main" : "error.main"}
                                        >
                                            {item.price >= 0
                                                ? `+ $${(item.price || 0).toLocaleString("es-CO")}`
                                                : `- $${Math.abs(item.price || 0).toLocaleString("es-CO")}`}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row">
                                        {canEdit && (
                                            <IconButton
                                                onClick={() => {
                                                    setCurrent(item);
                                                    setOpen(true);
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {canDelete && (
                                            <IconButton
                                                color="error"
                                                onClick={() => {
                                                    if (window.confirm(`¿Estás seguro de que deseas eliminar la adición "${item.name}"?`)) {
                                                        deleteItem(item.id!);
                                                    }
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            )}

            <ItemDialog<Addition>
                open={open}
                onClose={() => setOpen(false)}
                initial={
                    current || {
                        name: "",
                        category: "",
                        price: 0,
                        isIntrusive: false,
                    }
                }
                fields={[
                    { key: "name", label: "Nombre" },
                    {
                        key: "category",
                        label: "Categoría",
                        type: "select",
                        options: categories.map((c) => ({ value: c.name, label: c.name })),
                    },
                    { key: "price", label: "Precio (Positivo suma, Negativo resta)", type: "currency" },
                    { key: "isIntrusive", label: "Adición Intrusiva (Modal Pop-up en Carrito)", type: "boolean" },
                ]}
                onSubmit={async (values) => {
                    if (current?.id) await updateItem(current.id, values);
                    else await createItem(values);
                    setOpen(false);
                }}
            />
        </>
    );
}
